import React, { useEffect } from 'react';
import { Card, Descriptions, Button, Space, Tag, Typography, message, Row, Col, Skeleton, Alert } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { fetchLeadById, updateLeadStatus } from '../store/slices/leadsSlice';
import PageShell from '../components/Layout/PageShell';
import type { Lead } from '../types';

const { Paragraph, Text } = Typography;

const STATUS_FLOW: { key: Lead['status']; label: string }[] = [
  { key: 'new', label: 'New' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'converted', label: 'Converted' },
  { key: 'closed', label: 'Closed' },
  { key: 'spam', label: 'Spam' },
];

function leadStatusTag(status: string | undefined) {
  if (!status) return <Tag color="default">—</Tag>;
  const label = status.replace(/_/g, ' ').toUpperCase();
  const color =
    status === 'new'
      ? 'blue'
      : status === 'contacted'
        ? 'orange'
        : status === 'in_progress'
          ? 'processing'
          : status === 'converted'
            ? 'success'
            : status === 'closed'
              ? 'default'
              : status === 'spam'
                ? 'red'
                : 'default';
  return <Tag color={color}>{label}</Tag>;
}

function leadContactName(lead: Lead): string {
  if (lead.guestInfo?.name?.trim()) return lead.guestInfo.name.trim();
  const u = lead.user;
  if (u && typeof u === 'object' && 'profile' in u) {
    const p = (u as { profile?: { firstName?: string; lastName?: string } }).profile;
    const n = `${p?.firstName ?? ''} ${p?.lastName ?? ''}`.trim();
    if (n) return n;
  }
  return 'Unknown contact';
}

function leadContactEmail(lead: Lead): string {
  if (lead.guestInfo?.email?.trim()) return lead.guestInfo.email.trim();
  const u = lead.user;
  if (u && typeof u === 'object' && 'email' in u && typeof (u as { email?: string }).email === 'string') {
    return (u as { email: string }).email;
  }
  return '—';
}

function leadContactPhone(lead: Lead): string {
  if (lead.guestInfo?.phone?.trim()) return lead.guestInfo.phone.trim();
  const u = lead.user;
  if (u && typeof u === 'object' && 'profile' in u) {
    const ph = (u as { profile?: { phone?: string } }).profile?.phone;
    if (ph?.trim()) return ph.trim();
  }
  return '—';
}

function listingTitle(lead: Lead): string {
  const l = lead.listing;
  if (l && typeof l === 'object' && 'title' in l && typeof (l as { title?: string }).title === 'string') {
    return (l as { title: string }).title;
  }
  return '—';
}

function storeName(lead: Lead): string {
  const s = lead.store;
  if (s && typeof s === 'object' && 'storeName' in s && typeof (s as { storeName?: string }).storeName === 'string') {
    return (s as { storeName: string }).storeName;
  }
  return '—';
}

const LeadDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const { currentLead, loading, error } = useAppSelector((state) => state.leads);

  useEffect(() => {
    if (!id) return;
    dispatch(fetchLeadById(id));
  }, [dispatch, id]);

  const lead = currentLead && id && currentLead._id === id ? currentLead : null;

  const handleStatusUpdate = async (newStatus: Lead['status']) => {
    if (!id) return;
    try {
      await dispatch(updateLeadStatus({ leadId: id, data: { status: newStatus } })).unwrap();
      message.success(`Lead marked as ${newStatus.replace(/_/g, ' ')}`);
    } catch {
      message.error('Could not update status');
    }
  };

  if (!id) {
    return (
      <PageShell title="Lead" description="Missing lead id." titleEnd={null}>
        <Alert type="error" message="Invalid link" showIcon />
        <Button type="primary" className="mt-4" onClick={() => navigate('/leads')}>
          Back to Leads
        </Button>
      </PageShell>
    );
  }

  if (loading && !lead) {
    return (
      <PageShell title="Lead details" description="Loading this enquiry." titleEnd={null}>
        <Card className="vendor-card-flush rounded-xl border border-slate-200/80 shadow-sm">
          <Skeleton active paragraph={{ rows: 6 }} />
        </Card>
      </PageShell>
    );
  }

  if (error || !lead) {
    return (
      <PageShell title="Lead" description="We could not load this lead." titleEnd={null}>
        <Alert type="error" message={error || 'Lead not found'} showIcon className="mb-4" />
        <Button onClick={() => navigate('/leads')} className="border-slate-200 text-slate-800">
          Back to Leads
        </Button>
      </PageShell>
    );
  }

  return (
    <PageShell
      title="Lead details"
      description={`${listingTitle(lead)} · ${storeName(lead)}`}
      titleEnd={
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/leads')}
          className="vendor-form-back border-slate-200 text-slate-700 hover:!border-[#0171BF] hover:!text-[#0171BF]"
        >
          All leads
        </Button>
      }
    >
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title="Contact & enquiry" className="vendor-card-flush mb-4 rounded-xl border border-slate-200/80 shadow-sm">
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Name">{leadContactName(lead)}</Descriptions.Item>
              <Descriptions.Item label="Email">{leadContactEmail(lead)}</Descriptions.Item>
              <Descriptions.Item label="Phone">{leadContactPhone(lead)}</Descriptions.Item>
              {lead.guestInfo?.city ? (
                <Descriptions.Item label="Location">
                  {[lead.guestInfo.city, lead.guestInfo.state].filter(Boolean).join(', ')}
                </Descriptions.Item>
              ) : null}
              <Descriptions.Item label="Listing">{listingTitle(lead)}</Descriptions.Item>
              <Descriptions.Item label="Store">{storeName(lead)}</Descriptions.Item>
              <Descriptions.Item label="Preferred contact">{lead.preferredContactMethod ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Interested in">{lead.interestedIn?.replace(/_/g, ' ') ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Status">{leadStatusTag(lead.status)}</Descriptions.Item>
              <Descriptions.Item label="Received">
                {lead.createdAt ? dayjs(lead.createdAt).format('MMM D, YYYY h:mm A') : '—'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Message" className="vendor-card-flush rounded-xl border border-slate-200/80 shadow-sm">
            <Paragraph className="!mb-0 text-slate-700">{lead.message?.trim() || 'No message provided'}</Paragraph>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="Pipeline" className="vendor-card-flush mb-4 rounded-xl border border-slate-200/80 shadow-sm">
            <Text type="secondary" className="mb-3 block text-sm">
              Move this lead through your workflow. Changes sync to your lead list.
            </Text>
            <Space direction="vertical" className="w-full" size="small">
              {STATUS_FLOW.map(({ key, label }) => (
                <Button
                  key={key}
                  block
                  type={lead.status === key ? 'primary' : 'default'}
                  disabled={lead.status === key}
                  loading={loading}
                  className={
                    lead.status === key
                      ? 'border-0 bg-gradient-to-r from-[#0171BF] to-[#F7941F] !text-white shadow-sm'
                      : 'border-slate-200 text-slate-800'
                  }
                  onClick={() => handleStatusUpdate(key)}
                >
                  {label}
                </Button>
              ))}
            </Space>
          </Card>

          {lead.vendorResponse?.responded ? (
            <Card title="Your last response" className="vendor-card-flush rounded-xl border border-slate-200/80 shadow-sm">
              <Text type="secondary" className="block text-xs">
                {lead.vendorResponse.respondedAt
                  ? dayjs(lead.vendorResponse.respondedAt).format('MMM D, YYYY h:mm A')
                  : ''}
              </Text>
              <Paragraph className="!mb-0 mt-2 text-slate-700">{lead.vendorResponse.responseMessage || '—'}</Paragraph>
            </Card>
          ) : null}
        </Col>
      </Row>
    </PageShell>
  );
};

export default LeadDetails;
